import { useTranslation } from 'react-i18next'
import { useCallback } from 'react'
import {
  invalidateDocumentCategoryCache,
  useDeleteDocumentCategory,
  useAdminGetDocumentCategories,
} from '../../api/document-category'
import { UpdateDocumentCategoryDialog } from 'src/components/document-categories/system-admin/UpdateDocumentCategoryDialog'
import { toast } from 'react-toastify'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { DocumentCategoryTable } from 'src/components/document-categories/DocumentCategoryTable'
import { UserType } from 'src/components/document-categories/constants'
import { SwitchForcePushStatusDialog } from 'src/components/document-categories/system-admin/SwitchForcePushStatusDialog'
import { AxiosError } from 'axios'
import { CenteredSpinner } from '@lovoldsystem/ui'

export const SystemAdminDocumentCategoryTableContainer: React.FC = () => {
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const { t: translate } = useTranslation(['documentcategory', 'common'])
  const { mutateAsync } = useDeleteDocumentCategory()
  const { data, isLoading } = useAdminGetDocumentCategories()

  const onDeleteDocumentCategory = useCallback(
    (id: EntityId) => {
      showConfirmDialog(
        translate('deleteDocumentCategory', { ns: 'document-category' }),
        translate('areYouSureDeleteDocumentCategory', { ns: 'document-category' }),
        {
          acceptText: translate('yes', { ns: 'common' }),
          cancelText: translate('no', { ns: 'common' }),
        },

        async pop => {
          mutateAsync(id, {
            onSuccess: () => {
              toast.success(translate('successfullyRemoved', { ns: 'common-api-message' }))
            },
            onError: (error: AxiosError) => {
              if (error && error['data'] == 'CannotDeleteDocumentCategory') {
                toast.error(translate('CannotDeleteDocumentCategory', { ns: 'document-category' }))
              }
              if (error && error['data'] == 'CannotDeleteSystemGeneratedDocumentCategory') {
                toast.error(translate('cannotDeleteSystemGeneratedDocumentCategory', { ns: 'document-category' }))
              }
            },
          })

          invalidateDocumentCategoryCache.useGetDocumentCategories()
          pop()
        },
      )
    },
    [showConfirmDialog, translate, mutateAsync],
  )

  const onUpdate = useCallback(
    async id => {
      await showDialog(
        UpdateDocumentCategoryDialog,
        {
          componentProps: { id: id as EntityId },
        },
        undefined,
        true,
      )
    },
    [showDialog],
  )

  const handlePush = useCallback(
    async (id: EntityId) => {
      await showDialog(
        SwitchForcePushStatusDialog,
        {
          componentProps: { id: id as EntityId, isForcePushed: true },
        },
        undefined,
        true,
      )
    },
    [showDialog],
  )

  const handleOptionalize = useCallback(
    async (id: EntityId) => {
      await showDialog(
        SwitchForcePushStatusDialog,
        {
          componentProps: { id: id as EntityId, isForcePushed: false },
        },
        undefined,
        true,
      )
    },
    [showDialog],
  )

  if (isLoading) return <CenteredSpinner size="medium" />

  return (
    <DocumentCategoryTable
      userType={UserType.SystemAdmin}
      data={data?.items ?? []}
      onEdit={onUpdate}
      onDelete={onDeleteDocumentCategory}
      onPush={handlePush}
      onOptionalize={handleOptionalize}
    />
  )
}
